import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '@/components/layouts/AdminLayout';
import { adminApi } from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  Building2,
  TrendingUp,
  Clock,
  Mail,
  AlertTriangle,
  Loader2,
  RefreshCw,
  ChevronRight,
} from 'lucide-react';

const AdminDashboard = () => {
  const [metrics, setMetrics] = useState(null);
  const [activity, setActivity] = useState([]);
  const [pipelineQueue, setPipelineQueue] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      // Fetch each endpoint independently to avoid one failure breaking all
      const [metricsRes, activityRes, queueRes] = await Promise.allSettled([
        adminApi.getMetrics(),
        adminApi.getActivity(20),
        adminApi.getPipelineQueue()
      ]);
      
      if (metricsRes.status === 'fulfilled') {
        setMetrics(metricsRes.value.data);
      }
      if (activityRes.status === 'fulfilled') {
        setActivity(activityRes.value.data);
      }
      if (queueRes.status === 'fulfilled') {
        setPipelineQueue(queueRes.value.data);
      }
      
      // Only show error if all requests failed
      const allFailed = [metricsRes, activityRes, queueRes].every(r => r.status === 'rejected');
      if (allFailed) {
        toast.error('Failed to load dashboard data');
      }
    } catch (error) {
      console.error('Dashboard fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  const metricCards = [
    {
      label: 'Total Orgs',
      value: metrics?.total_orgs || 0,
      icon: Building2,
      color: 'text-primary',
      bgColor: 'bg-primary/10',
    },
    {
      label: 'Live',
      value: metrics?.live || 0,
      icon: TrendingUp,
      color: 'text-accent',
      bgColor: 'bg-accent/10',
    },
    {
      label: 'Pending',
      value: metrics?.pending || 0,
      icon: Clock,
      color: 'text-warning',
      bgColor: 'bg-warning/10',
    },
    {
      label: 'Invited',
      value: metrics?.invited || 0,
      icon: Mail,
      color: 'text-info',
      bgColor: 'bg-info/10',
    },
    {
      label: 'Pipeline Errors',
      value: metrics?.pipeline_errors || 0,
      icon: AlertTriangle,
      color: metrics?.pipeline_errors > 0 ? 'text-destructive' : 'text-muted-foreground',
      bgColor: metrics?.pipeline_errors > 0 ? 'bg-destructive/10' : 'bg-muted/10',
    },
  ];

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getActionLabel = (action) => {
    const labels = {
      'admin_login': 'Logged in',
      'org_created': 'Created organisation',
      'org_updated': 'Updated organisation',
      'pipeline_triggered': 'Triggered pipeline',
      'invite_sent': 'Sent CHRO invite',
    };
    return labels[action] || action;
  };

  if (loading) {
    return (
      <AdminLayout title="Dashboard">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Dashboard">
      <div className="space-y-6">
        {/* Metrics Bar */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 stagger-children">
          {metricCards.map((metric) => (
            <Card key={metric.label} className="metric-card card-interactive">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      {metric.label}
                    </p>
                    <p className="text-2xl font-bold mt-1" data-testid={`metric-${metric.label.toLowerCase().replace(' ', '-')}`}>
                      {metric.value}
                    </p>
                  </div>
                  <div className={`p-2 rounded-lg ${metric.bgColor}`}>
                    <metric.icon className={`h-5 w-5 ${metric.color}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Activity */}
          <Card className="glass-light">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-base font-semibold">Recent Activity</CardTitle>
              <Button variant="ghost" size="sm" onClick={fetchData}>
                <RefreshCw className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 max-h-80 overflow-y-auto">
                {activity.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    No recent activity
                  </p>
                ) : (
                  activity.map((item, index) => (
                    <div
                      key={item.id || index}
                      className="flex items-start gap-3 py-2 border-b border-border/50 last:border-0"
                    >
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                        <span className="text-xs font-medium text-primary">
                          {item.user_email?.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm">
                          <span className="font-medium">{item.user_email?.split('@')[0]}</span>
                          <span className="text-muted-foreground"> {getActionLabel(item.action)}</span>
                        </p>
                        {item.details?.org_name && (
                          <p className="text-xs text-muted-foreground truncate">
                            {item.details.org_name}
                          </p>
                        )}
                      </div>
                      <span className="text-xs text-muted-foreground whitespace-nowrap">
                        {formatTimestamp(item.timestamp)}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>

          {/* Pipeline Queue */}
          <Card className="glass-light">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-base font-semibold">Pipeline Queue</CardTitle>
              <Badge variant="outline" className="text-xs">
                {pipelineQueue.length} active
              </Badge>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 max-h-80 overflow-y-auto">
                {pipelineQueue.length === 0 ? (
                  <div className="text-center py-8">
                    <Clock className="h-8 w-8 text-muted-foreground mx-auto mb-2 opacity-50" />
                    <p className="text-sm text-muted-foreground">No pipelines running</p>
                  </div>
                ) : (
                  pipelineQueue.map((item) => (
                    <Link
                      key={item.org_id}
                      to={`/admin/orgs/${item.org_id}`}
                      className="flex items-center gap-3 p-3 rounded-lg bg-background/50 hover:bg-background transition-colors"
                    >
                      <div className="spinner" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{item.org_name}</p>
                        <p className="text-xs text-muted-foreground">
                          {item.current_stage}
                        </p>
                      </div>
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </Link>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <Card className="glass-light">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              <Button asChild data-testid="new-org-btn">
                <Link to="/admin/orgs?new=true">
                  <Building2 className="h-4 w-4 mr-2" />
                  New Organisation
                </Link>
              </Button>
              <Button variant="secondary" asChild>
                <Link to="/admin/orgs">
                  View All Organisations
                  <ChevronRight className="h-4 w-4 ml-2" />
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
